import type { ReactNode } from 'react';
import { cn } from './cn';

/** 12 pastel colours, one per row in turn (PMAI `PROJECT_COLORS`, FR-OVR-015). */
const COLORS = [
  'bg-blue-200 text-blue-800',
  'bg-green-200 text-green-900',
  'bg-amber-200 text-amber-900',
  'bg-red-200 text-red-900',
  'bg-purple-200 text-purple-900',
  'bg-orange-200 text-orange-900',
  'bg-cyan-200 text-cyan-900',
  'bg-pink-200 text-pink-900',
  'bg-lime-200 text-lime-900',
  'bg-yellow-200 text-yellow-900',
  'bg-indigo-200 text-indigo-900',
  'bg-teal-200 text-teal-900',
] as const;
const MUTED = 'bg-slate-200 font-medium text-slate-400';

export interface TimelineColumn {
  key: string;
  label: string;
  /** Second header line (column total). */
  sub: string;
}

/** A filled cell; `muted` — grey block without a value colour. */
export interface TimelineBlock {
  text: string;
  muted?: boolean;
}

export interface TimelineRow {
  key: string;
  label: ReactNode;
  cells: readonly (TimelineBlock | null)[];
}

type Join = 'single' | 'start' | 'middle' | 'end';

function joinOf(cells: TimelineRow['cells'], i: number): Join {
  const prev = i > 0 && cells[i - 1] !== null;
  const next = i < cells.length - 1 && cells[i + 1] !== null;
  return prev ? (next ? 'middle' : 'end') : next ? 'start' : 'single';
}

const JOIN_SHAPE: Record<Join, string> = {
  single: 'mx-0.5 rounded-md',
  start: 'ml-0.5 rounded-l-md',
  middle: '',
  end: 'mr-0.5 rounded-r-md',
};

/**
 * Rows × periods with coloured blocks; neighbouring filled cells join into one bar (Roadmap,
 * FR-OVR-013…016). Cells expose `data-column`, blocks `data-join` and `data-tone` (tests).
 */
export function Timeline({
  label,
  rowHeader,
  columns,
  rows,
}: {
  label: string;
  rowHeader: string;
  columns: readonly TimelineColumn[];
  rows: readonly TimelineRow[];
}) {
  return (
    <div className="overflow-x-auto rounded-card border border-border bg-surface">
      <table aria-label={label} className="w-max min-w-full border-collapse text-sm tabular-nums">
        <thead className="bg-canvas/70 text-xs text-ink-muted">
          <tr>
            <th scope="col" className="sticky left-0 z-10 min-w-48 bg-canvas px-3 py-2 text-left font-medium">
              {rowHeader}
            </th>
            {columns.map((c) => (
              <th key={c.key} scope="col" data-column={c.key} className="min-w-20 px-1 py-2 text-center">
                <span className="block font-medium text-ink">{c.label}</span>
                <span className="block font-semibold" data-total>
                  {c.sub}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, rowIndex) => (
            <tr key={r.key} className={cn('border-t border-border', rowIndex % 2 === 1 && 'bg-canvas/40')}>
              <th
                scope="row"
                className="sticky left-0 z-10 max-w-64 truncate bg-surface px-3 py-1.5 text-left font-medium"
              >
                {r.label}
              </th>
              {r.cells.map((cell, i) => {
                const join = joinOf(r.cells, i);
                return (
                  <td key={columns[i]?.key ?? i} data-column={columns[i]?.key} className="h-9 p-0 py-1">
                    {cell && (
                      <div
                        data-join={join}
                        data-tone={cell.muted ? 'muted' : String(rowIndex % COLORS.length)}
                        className={cn(
                          'flex h-full items-center justify-center px-1 text-xs font-bold',
                          cell.muted ? MUTED : COLORS[rowIndex % COLORS.length],
                          JOIN_SHAPE[join],
                        )}
                      >
                        {cell.text}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
